// Helpers for SourceDocumentModal: maps a RAG source chunk's document-level character
// offsets (chunk_start/chunk_end, set at ingestion by rag/embedding/dataprocessing.py) to
// a PDF page number and an in-page highlight range, by re-extracting text from the PDF
// client-side via pdf.js and normalizing whitespace the same way the ingestion pipeline
// did, so offsets computed server-side line up with what's rendered here.
import { pdfjs } from "react-pdf"
import type {
    TextContent,
    TextItem,
    TextMarkedContent
} from "pdfjs-dist/types/src/display/api"

pdfjs.GlobalWorkerOptions.workerSrc =
    process.env.NODE_ENV === "development"
        ? new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString()
        : `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`

export type MessageSource = {
    document_id: string
    document_title: string
    chunk_text: string
    similarity: number
    chunk_start?: number | null
    chunk_end?: number | null
}

export type PageTextItemRange = {
    start: number
    end: number
}

export type PageHighlightIndex = {
    pageStart: number
    pageEnd: number
    itemRanges: Array<PageTextItemRange | null>
}

const normalizeText = (text: string): string =>
    text.replace(/\s+/g, " ").trim()

const isTextItem = (item: TextItem | TextMarkedContent): item is TextItem =>
    "str" in item && typeof item.str === "string"

export const getSourceKey = (
    source: MessageSource,
    sourceIndex: number
): string => `${source.document_id}-${sourceIndex}-${source.chunk_text.slice(0, 24)}`

export const resolveBestPageForOffsets = (
    chunkStart: number,
    chunkEnd: number,
    pageIndexes: PageHighlightIndex[]
): number => {
    let bestPage = 1
    let bestOverlap = -1

    pageIndexes.forEach((page, index) => {
        const overlap = Math.max(
            0,
            Math.min(chunkEnd, page.pageEnd) - Math.max(chunkStart, page.pageStart)
        )

        if (overlap > bestOverlap) {
            bestOverlap = overlap
            bestPage = index + 1
        }
    })

    return bestPage
}

export const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;")

export const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value)

export async function buildPageHighlightIndex(documentUrl: string): Promise<{
    pageCount: number
    indexes: PageHighlightIndex[]
}> {
    const loadingTask = pdfjs.getDocument(documentUrl)
    const pdfDocument = await loadingTask.promise

    const normalizedPageChunks: Array<{
        pageLength: number
        itemRanges: Array<PageTextItemRange | null>
    }> = []

    for (let page = 1; page <= pdfDocument.numPages; page += 1) {
        const pageProxy = await pdfDocument.getPage(page)
        const textContent = (await pageProxy.getTextContent()) as TextContent

        let localCursor = 0
        const itemRanges: Array<PageTextItemRange | null> = []

        for (const item of textContent.items) {
            if (!isTextItem(item)) {
                itemRanges.push(null)
                continue
            }

            const normalized = normalizeText(item.str)
            if (normalized.length === 0) {
                itemRanges.push(null)
                continue
            }

            if (localCursor > 0) {
                localCursor += 1
            }

            const start = localCursor
            localCursor += normalized.length
            const end = localCursor
            itemRanges.push({ start, end })
        }

        normalizedPageChunks.push({
            pageLength: localCursor,
            itemRanges
        })
    }

    let documentCursor = 0
    const pageIndexes: PageHighlightIndex[] = normalizedPageChunks.map(
        (pageChunk, index) => {
            const pageStart = documentCursor
            const pageEnd = pageStart + pageChunk.pageLength

            const hasNextPage = index < normalizedPageChunks.length - 1
            const nextHasContent =
                hasNextPage && normalizedPageChunks[index + 1].pageLength > 0
            if (hasNextPage && pageChunk.pageLength > 0 && nextHasContent) {
                documentCursor = pageEnd + 1
            } else {
                documentCursor = pageEnd
            }

            return {
                pageStart,
                pageEnd,
                itemRanges: pageChunk.itemRanges
            }
        }
    )

    return {
        pageCount: pdfDocument.numPages,
        indexes: pageIndexes
    }
}
